import {
  AmountUnitTypeEnum,
  IOfferingRes,
  IPriceResSchema,
  IPriceUnderProductSchemaUI,
  IQuoteItemReqSchema,
  IQuoteItemRespSchema,
  IQuoteOfferingReqSchema,
  IQuotePrice,
  IQuoteTemplateConfigSchema,
  IRateResSchema,
  PriceModelEnum,
  ProductQuantityDisplayTypeEnum,
  ProductTypeEnum,
} from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import toString from 'lodash/toString';
import {
  FunctionComponent as FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Control,
  Controller,
  UseFormSetError,
  UseFormSetValue,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { MdInfo } from 'react-icons/md';
import {
  MBox,
  MCustomNumberInput,
  MDivider,
  MFlex,
  MFormField,
  MGrid,
  MIcon,
  MTooltip,
} from '../../../../../components/Monetize';
import { MEditableTextarea } from '../../../../../components/Monetize/MEditableTextarea';
import { MProductDisplay } from '../../../../../components/Monetize/MProductDisplay';
import {
  COMMA_QTY_FORMAT,
  CostCell,
  QuantityCell,
  QuantityDropdown,
} from '../../../../../components/Quotes';
import UnitPrice from '../../../../../components/Quotes/UnitPrice';
import {
  ONETIME_CHARGE_PRODUCT_TYPES,
  QUOTE_GRID_TEMPLATE_COLUMNS,
  QUOTE_QUANTITY_CELL_WIDTH,
  QUOTE_QUANTITY_TEXT_INSET,
} from '../../../../../constants/quotes';
import { useExperienceConfiguration } from '../../../../../hooks';
import { useFlags } from '../../../../../services/launchDarkly';
import {
  CUSTOMER_AUTOMATION_Anaplan_shouldLockCustomPrice,
  CUSTOMER_AUTOMATION_Coveo_shouldLockCustomPrice,
  CUSTOMER_AUTOMATION_CustomField_shouldLockCustomPrice,
  CUSTOMER_AUTOMATION_CustomField_shouldLockQuantity,
  CUSTOMER_AUTOMATION_Fexa_shouldLockCustomPrice,
  CUSTOMER_AUTOMATION_Fexa_shouldLockQuantity,
  CUSTOMER_AUTOMATION_LangChain_shouldLockCustomPrice,
  CUSTOMER_AUTOMATION_liveEo_shouldLockCustomPrice,
  CUSTOMER_AUTOMATION_QuoteOffering_ShouldLockQuantity,
  CUSTOMER_AUTOMATION_RedOak_shouldLockQuantity,
  teleport_ShouldLockQuantity,
} from '../../../../../types';
import { getQuoteType } from '../../../../../utils';
import {
  formatQuantityRangeTooltip,
  getQuoteColumnAvailabilityReview,
  getQuotePricesForQuoteItem,
  isNonProratedAdvanceProduct,
} from '../../../../../utils/quotes';
import { useQuoteContext } from '../../quoteContext';
import { QuoteItemDiscountInputAndValue } from './QuoteItemDiscountAndValue';
import QuoteItemNotePopover from './QuoteItemNotePopover';
import QuotePriceConfigPopover from './QuotePriceConfigPopover';
import { useDiscountDisabledProducts } from './useDiscountDisabledProducts';

/**
 * Quote Edit Page
 */
interface QuoteItemProps {
  id: string;
  index: number;
  quoteOfferingId: string;
  parentQuoteOfferingId?: string;
  item: IQuoteItemReqSchema;
  itemFromServer: IQuoteItemRespSchema;
  priorScheduledQuoteItem?: IQuoteItemRespSchema;
  rateId?: string | null;
  offering: IOfferingRes;
  offeringRate: IRateResSchema | null;
  control: Control<IQuoteOfferingReqSchema, object>;
  watchRateId: string | null | undefined;
  isRemoved?: boolean;
  isReadOnly?: boolean;
  quotePrices: IQuotePrice[];
  displayConfig?: IQuoteTemplateConfigSchema | null;
  isQOAddedToAmendment?: boolean;
  isDisabled: boolean;
  orderedPricesUnderProduct: IPriceUnderProductSchemaUI[];
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
  setError: UseFormSetError<IQuoteOfferingReqSchema>;
  handleQuoteOffering: () => void;
  handleQuoteOfferingWithoutDirtyCheck: () => void;
}

const IncrementalQuantityTooltip = () => (
  <MTooltip
    label={
      'If the selected product is non-prorated, you can optionally enter an incremental quantity here which will be added to the base quantity.'
    }
    placement="bottom-start"
  >
    <MBox pt="1">
      <MIcon as={MdInfo} color="tBlue.base" ml="1" />
    </MBox>
  </MTooltip>
);

const QuoteItem: FC<QuoteItemProps> = ({
  index,
  item,
  itemFromServer,
  priorScheduledQuoteItem,
  offering,
  offeringRate,
  control,
  quoteOfferingId: offeringId,
  parentQuoteOfferingId,
  watchRateId,
  isRemoved,
  isReadOnly: isReadOnlyProps = false,
  quotePrices,
  displayConfig,
  isQOAddedToAmendment,
  isDisabled,
  orderedPricesUnderProduct,
  setValue,
  setError,
  handleQuoteOffering,
  handleQuoteOfferingWithoutDirtyCheck,
}: QuoteItemProps) => {
  const {
    teleportSalesDemoTempDependentPricingConfig,
    customerAutomationAcuityMd,
    customerAutomationTeleport,
    enableQuantityDropDown,
    customerAutomationLiveEo,
    customerAutomationLangChain,
    customerAutomationCoveo,
    customerAutomationFexa,
    customerAutomationRedOak,
    customerAutomationCustomFieldQuantity,
    customerAutomationAnaplan,
  } = useFlags();

  const { enableQuoteItemNotes, showCommaFormattedQty } =
    useExperienceConfiguration(
      ExperienceConfigurationKeys.quoteAndContractSettings,
    );

  const { discountDisabledProductIds } = useDiscountDisabledProducts(
    offering.offeringProducts,
  );

  const usagePrices = useMemo(() => {
    if (
      itemFromServer.productType === ProductTypeEnum.USAGE &&
      offeringRate?.id &&
      quotePrices?.length
    ) {
      return getQuotePricesForQuoteItem({
        parentQuoteOfferingId,
        quoteOfferingId: itemFromServer.quoteOfferingId,
        quotePrices,
        productId: item.productId,
        rateId: offeringRate?.id,
      });
    } else {
      return null;
    }
  }, [
    itemFromServer.productType,
    itemFromServer.quoteOfferingId,
    offeringRate?.id,
    quotePrices,
    parentQuoteOfferingId,
    item.productId,
  ]);
  const allowedToConfigureUsageDiscount =
    (itemFromServer.productType === ProductTypeEnum.USAGE &&
      !!usagePrices &&
      usagePrices?.length === 1 &&
      !!usagePrices[0].amount) ||
    itemFromServer.productType !== ProductTypeEnum.USAGE;

  const itemQuantityDisplayType = offering.offeringProducts.find(
    (offeringProduct) => offeringProduct.product.id === item.productId,
  )?.quantityDisplayType;
  const showQuantityDropdown =
    enableQuantityDropDown?.productIds?.includes(item.productId) ||
    itemQuantityDisplayType === ProductQuantityDisplayTypeEnum.DROPDOWN;

  const [triggerVisibility, setTriggerVisibility] = useState<
    'hidden' | 'visible'
  >('hidden');
  const [isIncrementalEditing, setIsIncrementalEditing] = useState(false);

  const incrementalPriceRange =
    item.productId != null
      ? offeringRate?.productPriceRanges?.[item.productId]
      : undefined;
  const incrementalTooltipLabel = incrementalPriceRange
    ? formatQuantityRangeTooltip(incrementalPriceRange)
    : '';
  const {
    isLoading,
    quoteData: { quote },
    quoteStateData: { offeringLoadingState },
    useAmendmentV2,
  } = useQuoteContext();

  const isOfferingLoading =
    offeringLoadingState[offeringId]?.isLoading || false;
  const isRateLoading =
    offeringLoadingState[offeringId]?.isLoading &&
    offeringLoadingState[offeringId]?.modifiedItems
      ?.get?.('header')
      ?.has('rateId');
  const isOfferingDeleting =
    offeringLoadingState[offeringId]?.isDeleting ||
    (parentQuoteOfferingId &&
      offeringLoadingState[parentQuoteOfferingId]?.isDeleting) ||
    false;
  const { isAmendment, isRenewal } = getQuoteType(quote);
  const { errors } = useFormState({ control });

  const customDiscountType = useWatch({
    control,
    name: `items.${index}.customDiscountType`,
  });

  const quantity = useWatch({
    control,
    name: `items.${index}.quantity`,
  });

  const currentProduct = offering.products?.find(
    ({ id }) => id === item.productId,
  );

  const hasDerivedQuantityConfiguration =
    !!teleportSalesDemoTempDependentPricingConfig?.derivedQuantity?.[
      item.productId
    ];
  const isDiscountProduct =
    currentProduct?.productType === ProductTypeEnum.DISCOUNT;

  const lockQuantityEditFeatureFlag = useMemo(() => {
    return (
      CUSTOMER_AUTOMATION_QuoteOffering_ShouldLockQuantity({
        customerAutomationAcuityMd,
        offeringId: offering.id,
        productId: item.productId,
      }) ||
      teleport_ShouldLockQuantity({
        customerAutomationTeleport,
        productId: item.productId,
      }) ||
      CUSTOMER_AUTOMATION_Fexa_shouldLockQuantity({
        customerAutomationFexa,
        productId: item.productId,
      }) ||
      CUSTOMER_AUTOMATION_RedOak_shouldLockQuantity({
        customerAutomationRedOak,
        offeringId: offering.id,
        productId: item.productId,
      }) ||
      CUSTOMER_AUTOMATION_CustomField_shouldLockQuantity({
        customerAutomationCustomFieldQuantity,
        product: currentProduct,
      })
    );
  }, [
    customerAutomationAcuityMd,
    customerAutomationTeleport,
    customerAutomationFexa,
    customerAutomationRedOak,
    customerAutomationCustomFieldQuantity,
    currentProduct,
    item.productId,
    offering.id,
  ]);

  const lockCustomPriceEdit = useMemo(() => {
    return (
      CUSTOMER_AUTOMATION_liveEo_shouldLockCustomPrice({
        productId: item.productId,
        customerAutomationLiveEo,
      }) ||
      CUSTOMER_AUTOMATION_LangChain_shouldLockCustomPrice({
        productId: item.productId,
        customerAutomationLangChain,
      }) ||
      CUSTOMER_AUTOMATION_Coveo_shouldLockCustomPrice({
        productId: item.productId,
        customerAutomationCoveo,
      }) ||
      CUSTOMER_AUTOMATION_Fexa_shouldLockCustomPrice({
        productId: item.productId,
        customerAutomationFexa,
      }) ||
      CUSTOMER_AUTOMATION_CustomField_shouldLockCustomPrice({
        customerAutomationCustomFieldQuantity,
        product: currentProduct,
      }) ||
      CUSTOMER_AUTOMATION_Anaplan_shouldLockCustomPrice({
        customerAutomationAnaplan,
        offeringId: offering.id,
        productId: item.productId,
      })
    );
  }, [
    customerAutomationLiveEo,
    customerAutomationLangChain,
    customerAutomationCoveo,
    customerAutomationFexa,
    customerAutomationCustomFieldQuantity,
    customerAutomationAnaplan,
    currentProduct,
    item.productId,
    offering.id,
  ]);

  const isCustomPrice =
    offeringRate?.prices?.find(
      (price: IPriceResSchema) => price?.product?.id === item.productId,
    )?.priceModel === PriceModelEnum.CUSTOM;

  useEffect(() => {
    if (isCustomPrice && !quantity) {
      // set default quantity to 1 if the product has a custom price.
      // this is to prevent confusion when the price per unit is changed and the amount is 0 when the quantity is also zero.
      setValue(`items.${index}.quantity`, 1, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [isCustomPrice]);

  const discountAmountRef = useRef<any>(null);

  const onChangeDiscountType = (type: AmountUnitTypeEnum) => {
    setValue(`items.${index}.customDiscountAmountOrPercent`, 0, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue(`items.${index}.customDiscountType`, type, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue(`items.${index}.customDiscountLifecyclePolicies`, [], {
      shouldDirty: true,
      shouldValidate: true,
    });

    handleQuoteOfferingWithoutDirtyCheck();
  };

  const isNonProrated = useMemo(
    () => isNonProratedAdvanceProduct(currentProduct),
    [currentProduct],
  );

  if (!quote) {
    return null;
  }

  if (!currentProduct) {
    return null;
  }

  const { isDiscountNotAvailable, isQuantityNotAvailable } =
    getQuoteColumnAvailabilityReview(currentProduct.productType);
  const canDiscount =
    !isDiscountNotAvailable &&
    !discountDisabledProductIds.includes(item.productId);

  // Make it read-only if Amendment V2 and product is onetime
  const isReadOnly =
    isReadOnlyProps ||
    (useAmendmentV2 &&
      !isQOAddedToAmendment &&
      ONETIME_CHARGE_PRODUCT_TYPES.has(currentProduct?.productType));

  /**
   * Everything that permanently locks a quantity. These render read-only —
   * borderless, no grey fill — rather than disabled: a locked row otherwise
   * showed a greyed box or a bare value depending on which control it happened
   * to render, and a page this dense reads better without a grid of grey boxes.
   */
  const isQuantityReadOnly =
    isReadOnly ||
    lockQuantityEditFeatureFlag ||
    hasDerivedQuantityConfiguration ||
    isDiscountProduct ||
    (!!priorScheduledQuoteItem &&
      currentProduct?.productType === ProductTypeEnum.ONETIME);

  /** Mid-save states, where disabled is still the right signal. */
  const isQuantityBusy = isDisabled || isOfferingDeleting || !!isRateLoading;

  return (
    <>
      <MGrid
        templateColumns={QUOTE_GRID_TEMPLATE_COLUMNS}
        columnGap={6}
        py={2}
        pl="7"
        alignItems="center"
        onMouseEnter={() => {
          setTriggerVisibility('visible');
        }}
        onMouseLeave={() => {
          setTriggerVisibility('hidden');
        }}
      >
        <MBox
          data-testid="product-cell"
          pl={6}
          pr={12}
          alignSelf="center"
          overflow="hidden"
        >
          <MProductDisplay
            name={itemFromServer?.productName}
            productType={currentProduct?.productType!}
            subscriptionTiming={offeringRate?.subscriptionTiming}
            disabled={isRemoved}
            color="tPurple.dark"
          />
          <MFormField error={errors.items?.[index]?.description}>
            <Controller
              control={control}
              name={`items.${index}.description`}
              render={({ field: { onChange, value } }) => (
                <MEditableTextarea
                  value={value || ''}
                  isDisabled={
                    isOfferingDeleting ||
                    isRateLoading ||
                    isReadOnly ||
                    isRemoved
                  }
                  onSubmit={(currValue) => {
                    onChange(currValue);
                    handleQuoteOfferingWithoutDirtyCheck();
                  }}
                  onCancel={onChange}
                  noOfLines={3}
                  previewButtonProps={{ minH: value ? '3em' : '1em' }}
                />
              )}
            />
          </MFormField>
        </MBox>

        {/* Date placeholder */}

        {/* Incremental Quantity placeholder */}
        {!isQuantityNotAvailable && isNonProrated ? (
          <MFormField
            data-testid="incremental-quantity-cell"
            alignSelf="center"
            // v3's Field.Root aligns children to flex-start; stretch so the
            // wrapper spans the column and can center the control in it.
            alignItems="stretch"
            error={errors?.items?.[index]?.incrementalQuantity}
          >
            <Controller
              control={control}
              name={`items.${index}.incrementalQuantity`}
              render={({ field: { onChange, onBlur, value, ...rest } }) => {
                if (showQuantityDropdown) {
                  const numericValue =
                    value !== null &&
                    value !== undefined &&
                    !isNaN(Number(value))
                      ? Number(value)
                      : 0;
                  return (
                    <MBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <QuantityDropdown
                        index={index}
                        isDisabled={isQuantityBusy}
                        quote={quote}
                        item={itemFromServer}
                        placeholder="Quantity"
                        value={numericValue}
                        isScheduledChange={!!priorScheduledQuoteItem}
                        scheduledChangePriorQty={
                          priorScheduledQuoteItem?.quantity
                        }
                        isReadOnly={isQuantityReadOnly}
                        isAmendment={isAmendment}
                        isRenewal={isRenewal}
                        isRemoved={isRemoved}
                        onChange={(incrementalQuantity: number) => {
                          onChange(incrementalQuantity);
                          handleQuoteOfferingWithoutDirtyCheck();
                        }}
                        orderedPricesUnderProduct={orderedPricesUnderProduct}
                        {...rest}
                      />
                      <IncrementalQuantityTooltip />
                    </MBox>
                  );
                }

                return (
                  <MBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <MTooltip
                      isOpen={
                        isIncrementalEditing &&
                        !!incrementalTooltipLabel &&
                        !isAmendment
                      }
                      label={incrementalTooltipLabel}
                      placement="top"
                    >
                      <MCustomNumberInput
                        width="100%"
                        maxWidth={QUOTE_QUANTITY_CELL_WIDTH}
                        isDisabled={isQuantityBusy}
                        isReadOnly={isQuantityReadOnly}
                        variant={isQuantityReadOnly ? 'readonly' : 'outline'}
                        styleProps={QUOTE_QUANTITY_TEXT_INSET}
                        // Raw value in; zag groups it while not editing (a
                        // pre-formatted "1,500" parses as 1 — see QuantityCell).
                        value={value ?? 0}
                        formatOptions={
                          showCommaFormattedQty && !isIncrementalEditing
                            ? COMMA_QTY_FORMAT
                            : undefined
                        }
                        resetEmptyValueOnBlur
                        onFocus={() => setIsIncrementalEditing(true)}
                        onChange={(_valueAsString, valueAsNumber) => {
                          onChange(valueAsNumber);
                        }}
                        onBlur={() => {
                          setIsIncrementalEditing(false);
                          onBlur();
                          handleQuoteOfferingWithoutDirtyCheck();
                        }}
                        {...rest}
                      />
                    </MTooltip>
                    <IncrementalQuantityTooltip />
                  </MBox>
                );
              }}
            />
          </MFormField>
        ) : (
          <MBox data-testid="date-cell" />
        )}

        {!isQuantityNotAvailable ? (
          <MFormField
            data-testid="quantity-cell"
            error={errors?.items?.[index]?.quantity}
            alignSelf="center"
            alignItems="stretch"
          >
            <Controller
              control={control}
              name={`items.${index}.quantity`}
              render={({ field: { onChange, onBlur, value, ...rest } }) => {
                if (showQuantityDropdown && !isNonProrated) {
                  const numericValue =
                    value !== null &&
                    value !== undefined &&
                    !isNaN(Number(value))
                      ? Number(value)
                      : 0;
                  return (
                    <QuantityDropdown
                      index={index}
                      isDisabled={isQuantityBusy}
                      quote={quote}
                      item={itemFromServer}
                      placeholder="Quantity"
                      value={numericValue}
                      isScheduledChange={!!priorScheduledQuoteItem}
                      scheduledChangePriorQty={
                        priorScheduledQuoteItem?.quantity
                      }
                      isReadOnly={isQuantityReadOnly}
                      isAmendment={isAmendment}
                      isRenewal={isRenewal}
                      isRemoved={isRemoved}
                      onChange={(quantity: number) => {
                        onChange(quantity);
                        handleQuoteOfferingWithoutDirtyCheck();
                      }}
                      orderedPricesUnderProduct={orderedPricesUnderProduct}
                      {...rest}
                    />
                  );
                } else {
                  return (
                    <QuantityCell
                      index={index}
                      offeringRate={offeringRate}
                      isDisabled={isQuantityBusy}
                      quote={quote}
                      item={itemFromServer}
                      placeholder="Quantity"
                      value={toString(value ?? 0)}
                      isScheduledChange={!!priorScheduledQuoteItem}
                      scheduledChangePriorQty={
                        priorScheduledQuoteItem?.quantity
                      }
                      isReadOnly={isQuantityReadOnly || isNonProrated}
                      isAmendment={isAmendment}
                      isRenewal={isRenewal}
                      isRemoved={isRemoved}
                      onChange={(
                        _valueAsString: string,
                        valueAsNumber: number,
                      ) => {
                        onChange(valueAsNumber);
                      }}
                      handleBlur={(add: boolean) => {
                        onBlur();
                        // addition component does not trigger isDirty reliably
                        if (add) {
                          handleQuoteOfferingWithoutDirtyCheck();
                        } else {
                          handleQuoteOffering();
                        }
                      }}
                      handleQuoteOfferingWithoutDirtyCheck={
                        handleQuoteOfferingWithoutDirtyCheck
                      }
                      isLoading={isLoading}
                      isOfferingLoading={isOfferingLoading}
                      setError={setError}
                      setValue={setValue}
                      watchRateId={watchRateId}
                      offering={offering}
                      {...rest}
                    />
                  );
                }
              }}
            />
          </MFormField>
        ) : (
          <MBox data-testid="quantity-cell" />
        )}

        {canDiscount ? (
          <QuoteItemDiscountInputAndValue
            index={index}
            errors={errors}
            control={control}
            setValue={setValue}
            quote={quote}
            offeringId={offeringId}
            customDiscountType={customDiscountType}
            discountAmountRef={discountAmountRef}
            itemFromServer={itemFromServer}
            isDisabled={
              isRateLoading || isOfferingDeleting || isRemoved || isReadOnly
            }
            isReadOnly={isReadOnly || isRemoved}
            onChangeDiscountType={onChangeDiscountType}
            handleQuoteOffering={handleQuoteOfferingWithoutDirtyCheck}
            disableUnitTypeSelect={!allowedToConfigureUsageDiscount}
            disableUnitTypeMessage={
              !allowedToConfigureUsageDiscount
                ? 'Usage products with multiple tiers can only have percentage discount.'
                : ''
            }
            usagePrice={
              allowedToConfigureUsageDiscount && !!usagePrices
                ? usagePrices[0]
                : null
            }
          />
        ) : (
          <MBox data-testid="discount-cell" />
        )}

        <UnitPrice
          item={itemFromServer}
          quote={quote}
          errors={errors}
          control={control}
          index={index}
          isCustomPrice={isCustomPrice && !lockCustomPriceEdit}
          setValue={setValue}
          handleQuoteOfferingWithoutDirtyCheck={
            handleQuoteOfferingWithoutDirtyCheck
          }
          isDisabled={isRemoved || isRateLoading || isReadOnly}
          offeringRate={offeringRate}
          productType={currentProduct.productType}
          displayPrecision={currentProduct.ppuDisplayPrecision}
          styles={{
            color: 'tPurple.dark',
          }}
          quotePrices={quotePrices}
          displayConfig={displayConfig}
          parentQuoteOfferingId={parentQuoteOfferingId}
        />
        <CostCell item={itemFromServer} currency={quote?.currency} />

        <MFlex alignItems="center">
          <QuotePriceConfigPopover
            index={index}
            item={itemFromServer}
            setValue={setValue}
            handleQuoteOfferingWithoutDirtyCheck={
              handleQuoteOfferingWithoutDirtyCheck
            }
            isRemoved={isRemoved}
            isLoading={isLoading || isOfferingLoading}
            billingFrequency={offeringRate?.billingFrequency}
            triggerVisibility={
              itemFromServer.options?.displayUnitPriceFrequency
                ? 'visible'
                : triggerVisibility
            }
            isReadOnly={isReadOnly}
          />
          {enableQuoteItemNotes && (
            <QuoteItemNotePopover
              index={index}
              item={itemFromServer}
              setValue={setValue}
              handleQuoteOfferingWithoutDirtyCheck={
                handleQuoteOfferingWithoutDirtyCheck
              }
              isRemoved={isRemoved}
              isLoading={isLoading || isOfferingLoading}
              triggerVisibility={
                itemFromServer.notes ? 'visible' : triggerVisibility
              }
              isReadOnly={isReadOnly}
            />
          )}
        </MFlex>
      </MGrid>
      <MDivider />
    </>
  );
};
export default QuoteItem;
